import { Span, Text, type UseModalState } from "@clipboard-health/ui-react";
import { type AdminMessage } from "@sendbird/chat/message";
import { logError } from "@src/appV2/lib/analytics";

import { useChartingCredentialIntegrationConfig } from "../WorkerCredentials/api/useChartingCredentialIntegrationConfig";

interface SendbirdWorkerCredentialRequestedMessageProps {
  message: AdminMessage;
  workerName?: string;
  manageCredentialsModalState?: UseModalState;
}

function parseWorkerCredentialRequestedMessage(message: AdminMessage): {
  hasExistingCredentials: boolean;
} {
  try {
    return JSON.parse(message.message) as { hasExistingCredentials: boolean };
  } catch (error) {
    logError("Failed to parse worker credential requested message", {
      error,
      metadata: { message },
    });
    return { hasExistingCredentials: false };
  }
}

export function WorkerCredentialRequestedMessage(
  props: SendbirdWorkerCredentialRequestedMessageProps
) {
  const { message, workerName, manageCredentialsModalState } = props;
  const chartingCredentialIntegrationConfig = useChartingCredentialIntegrationConfig();
  if (!chartingCredentialIntegrationConfig.enabled) {
    return null;
  }

  const { software } = chartingCredentialIntegrationConfig;

  const { hasExistingCredentials } = parseWorkerCredentialRequestedMessage(message);
  const messageText = hasExistingCredentials
    ? `${workerName}'s ${software} account was reset.`
    : `A new ${software} account has been created for ${workerName}.`;

  return (
    <div className="sendbird-admin-message">
      <Text
        variant="body2"
        textAlign="center"
        className="sendbird-label--caption-2 worker-credential-requested-message"
      >
        {messageText}{" "}
        <Span
          role="button"
          tabIndex={0}
          className="worker-credential-requested-message-link"
          onClick={() => {
            manageCredentialsModalState?.openModal();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              manageCredentialsModalState?.openModal();
            }
          }}
        >
          View details
        </Span>
      </Text>
    </div>
  );
}
